import { useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'contexts/Localization'
import { CloseButton } from 'components/CloseButton'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import { Box, Text, Link } from 'vvs-uikit'
import Collapse from 'components/Collapse/Base'
import historyBackOrReplace from 'utils/historyBackOrReplace'
import { Path } from 'config/constants/paths'

import { LightPageContainer } from '../styles'
import {
  Banner,
  BannerContent,
  CloseBtnWrapper,
  FaqContent,
  FaqTitle,
  QuestionText,
  AnswerUl,
  AnswerSubUl,
  Hr,
} from './styles'

const QuestFAQ: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory<{ focus: string }>()
  const teamHealthRef = useRef<HTMLDivElement>()

  const focusOnTeamHealth = history.location.state?.focus === 'teamHealth'

  useEffect(() => {
    if (focusOnTeamHealth && teamHealthRef.current) {
      const timeout = setTimeout(() => {
        teamHealthRef.current.scrollIntoView({ behavior: 'smooth' })
      }, 100)
      return () => clearTimeout(timeout)
    }
    return null
  }, [focusOnTeamHealth])

  return (
    <LightPageContainer>
      <Banner bgImg="/images/vvsGotchi/bg_grass.png">
        <BannerContent>
          <PixelatedImage src="/images/vvsGotchi/mole_magic.png" alt="Quest FAQ banner" width="124px" />
          <CloseBtnWrapper>
            <CloseButton fill="black" onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_QUEST)} />
          </CloseBtnWrapper>
        </BannerContent>
      </Banner>
      <FaqContent>
        <FaqTitle as="h1">{t('Quest FAQ')}</FaqTitle>

        <Collapse title={<QuestionText>{t('What are quests?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('Interested in earning more yield on your VVS Crystal farms? Well, look no further!')}</li>
            <li>{t('Take your team of Miner Moles on various quests to earn Experience Points (EXP)')}</li>
            <li>{t('The goal is to earn as much EXP as possible and outrank your peers')}</li>
            <li>{t('The higher your rank, the higher your Mole’s farm boost factor')}</li>
            <li>{t('Are you ready to get started?')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse title={<QuestionText>{t('How do I get started?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('Firstly, you will need to own at least 1 Miner Mole')}</li>
            <li>
              {t(
                'Secondly, you will also need to create an Expedition Team before embarking on a quest. You can do so on the Team page on VVSGotchi.',
              )}
            </li>
            <li>{t('Once you have formed your team, you can now go on any quest you want!')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse title={<QuestionText>{t('How do I go on quests?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('Click on any quest, and a pop-up will appear')}</li>
            <li>{t('In the pop-up, you will be able to view quest instructions and a prompt to proceed')}</li>
            <li>{t('When you are ready to play, click on the prompt and you are good to go!')}</li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse title={<QuestionText>{t('How much EXP can I earn?')}</QuestionText>} arrowColor="black">
          <AnswerUl>
            <li>{t('EXP to be earned is shown on each quest card')}</li>
            <li>
              {t(
                'To note: some quests have variable EXP. For these quests, details on how much EXP to be earned can be found inside the individual quest card',
              )}
            </li>
            <li>
              {t(
                'To find out how much total EXP your Expedition Team can earn, multiply this by the number of Moles currently on your team',
              )}
            </li>
            <li>
              {t(
                'Remember, if you want to earn more yield on your farms, your goal should be to accumulate more EXP versus your peers. Let the games begin!',
              )}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('Where can I find information about quests I have completed?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t('If you would like to review your completed quests, click on the History link on the Quest page')}
            </li>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          ref={teamHealthRef}
          initIsExpand={focusOnTeamHealth}
          title={<QuestionText>{t('What is "+/-" EXP boost based on Team’s health”?')}</QuestionText>}
          arrowColor="black"
          contentWrapperProps={{ flexDirection: 'column' }}
        >
          <AnswerUl>
            <li>{t('Miner Moles love to go on Quests, but they need energy to do so')}</li>
            <li>{t('This is why maintaining the health of your Team is important!')}</li>
            <li>{t('The level of your Team’s health determines how much EXP boost you will earn')}</li>
            <li>{t('Here’s the breakdown of EXP boosts based on health levels:')}</li>
          </AnswerUl>

          <Box mb={3}>
            <img src="/images/vvsGotchi/health_meter_levels.png" alt={t('health meter levels')} />
          </Box>

          <AnswerUl>
            <li>{t('Excellent: 100')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: + 50%')}</li>
            </AnswerSubUl>
            <li>{t('Healthy: 71 - 99')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: + 35% more')}</li>
            </AnswerSubUl>
            <li>{t('Normal: 61 - 70')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: 0%')}</li>
            </AnswerSubUl>
            <li>{t('Unhealthy: 11 - 60')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: - 35%')}</li>
            </AnswerSubUl>
            <li>{t('Sick: Below 10')}</li>
            <AnswerSubUl>
              <li>{t('EXP boost: - 50%')}</li>
            </AnswerSubUl>
          </AnswerUl>
        </Collapse>
        <Hr />
        <Collapse
          title={<QuestionText>{t('I just completed a quest - why did my Mole(s) not earn EXP?')}</QuestionText>}
          arrowColor="black"
        >
          <AnswerUl>
            <li>
              {t(
                'If your Mole(s) did not earn any EXP after you have completed a quest, there may be two reasons for this:',
              )}
            </li>
            <AnswerSubUl>
              <li>
                {t(
                  'The Mole is not on your team. Add the Mole to your team so it can also start earning EXP when you go on quests!',
                )}
              </li>
              <li>
                {t(
                  'The Mole had previously completed a quest in one wallet before being transferred to another wallet during the same day. If you would like your Mole to earn EXP while in the new wallet, you will have to wait till the end of the day (defined as 00:00 UTC) to start a quest.',
                )}
              </li>
            </AnswerSubUl>
          </AnswerUl>
        </Collapse>

        <Text color="black" fontSize="13px" mt="100px" mb="40px">
          {t('For a step-by-step guide on how to play quests, click ')}
          <Link
            inline
            underline="always"
            bold
            href="https://docs.vvs.finance/product-guides/vvs-gotchi/quest-page"
            target="_blank"
          >
            {t('here')}
          </Link>
          .
        </Text>
      </FaqContent>
    </LightPageContainer>
  )
}

export default QuestFAQ
